import posthog from "posthog-js";
import { configurePostHogCapture, trackNavigationEvent } from "./browserAnalytics.js";

export { trackEvent, trackEventOnce } from "./browserAnalytics.js";

/**
 * Google OAuth leaves this origin immediately. Capture synchronously with a
 * navigation-safe beacon rather than using the ordinary asynchronous adapter.
 * Both destinations share one first-party event ID for parity checks.
 */
export function captureGoogleOAuthStartBeforeRedirect(redirectUri: string): void {
    const properties = {
        method: "google",
        redirect_uri: redirectUri,
    };
    const event = trackNavigationEvent("auth_google_oauth_started", properties);
    try {
        if (posthog.__loaded) {
            posthog.capture(
                "auth_google_oauth_started",
                {
                    ...properties,
                    ...(event
                        ? {
                              $insert_id: event.event_id,
                          }
                        : {}),
                },
                {
                    transport: "sendBeacon",
                    send_instantly: true,
                },
            );
        }
    } catch {
        /* Analytics must never prevent the OAuth redirect. */
    }
}

/**
 * Initialize PostHog for product analytics
 * Call this function as early as possible in the application lifecycle
 */
export function initPostHog() {
    // A disabled PostHog SDK must not gate or affect BigQuery events.
    configurePostHogCapture(undefined);
    // Check if PostHog is enabled
    const posthogEnabled = import.meta.env.VITE_POSTHOG_ENABLED !== "false";
    const posthogApiKey = import.meta.env.VITE_POSTHOG_API_KEY;
    const posthogHost = import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";

    if (!posthogEnabled) {
        console.log("[PostHog] Disabled via environment variable");
        return;
    }

    if (!posthogApiKey) {
        console.warn("[PostHog] API key not configured. Analytics disabled.");
        return;
    }

    posthog.init(posthogApiKey, {
        api_host: posthogHost,

        // Only create person profiles for identified users (authenticated)
        // This saves costs and aligns with our authentication flow
        person_profiles: "identified_only",

        // Autocapture configuration
        autocapture: true,
        capture_pageview: false, // Disabled - we handle pageviews manually in App.tsx for SPA routing
        capture_pageleave: true,

        // Session recording is OFF.
        //
        // remote_privacy_policy.md states, under "Privacy-focused
        // configuration": "Session recording is **disabled**". This is
        // deliberately a hard-coded constant rather than an env var — a
        // toggle would let a deploy silently contradict the published
        // policy. Turning replay on requires updating that document first,
        // and adding masking config: PostHog masks password inputs by
        // default but records everything else in the DOM, and this
        // dashboard shows device names and account details.
        disable_session_recording: true,

        // Development settings
        loaded: (posthog) => {
            if (import.meta.env.MODE === "development" && import.meta.env.VITE_POSTHOG_DEBUG === "true") {
                posthog.debug();
                console.log("[PostHog] Debug mode enabled");
            }
        },

        // Respect Do Not Track
        respect_dnt: true,

        // Advanced options
        persistence: "localStorage",
        cross_subdomain_cookie: false,

        // Disable in development unless explicitly enabled
        opt_out_capturing_by_default: import.meta.env.MODE === "development" && import.meta.env.VITE_POSTHOG_DEBUG !== "true",
    });

    // This adapter is optional for our first-party event source, but the
    // PostHog SDK itself remains eagerly initialized exactly as before.
    configurePostHogCapture(async (event, properties, name) => {
        await waitForPostHog();
        if (!posthog.__loaded) {
            return false;
        }
        posthog.capture(name, {
            ...properties,
            ...(event
                ? {
                      $insert_id: event.event_id,
                  }
                : {}),
        });
        return true;
    });

    // Register global properties
    posthog.register({
        app_version: "1.0.0",
        environment: import.meta.env.MODE,
        app_name: "desktop-commander-remote",
    });

    console.log("[PostHog] Initialized successfully");
}

/** Resolves once the PostHog script has loaded, or after 2s. Shared by identify and trackEvent. */
async function waitForPostHog() {
    const startTime = Date.now();
    while (!posthog.__loaded && Date.now() - startTime < 2000) {
        await new Promise((resolve) => setTimeout(resolve, 50));
    }
}

/**
 * Identify a user in PostHog
 * Call this when user logs in
 */
export async function identifyPostHogUser(userId: string, properties?: Record<string, any>) {
    if (typeof window === "undefined") {
        return;
    }

    // Same wait as trackEvent. Without it, an identify that lands before the PostHog script has
    // loaded is dropped silently: every event until the next identify attempt is then captured
    // against the anonymous id, and with person_profiles: "identified_only" those events never
    // become a person, so the whole onboarding sequence can go missing from person-level funnels.
    await waitForPostHog();

    if (posthog.__loaded) {
        posthog.identify(userId, properties);
    }
}

/**
 * Reset PostHog user identification
 * Call this when user logs out
 */
export function resetPostHogUser() {
    if (typeof window !== "undefined" && posthog.__loaded) {
        posthog.reset();
    }
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, any>) {
    if (typeof window !== "undefined" && posthog.__loaded) {
        posthog.people.set(properties);
    }
}

/**
 * Check if a feature flag is enabled
 */
export function isFeatureFlagEnabled(flagKey: string): boolean {
    if (typeof window !== "undefined" && posthog.__loaded) {
        return posthog.isFeatureEnabled(flagKey) || false;
    }
    return false;
}

/**
 * Get PostHog instance for advanced usage
 */
export function getPostHog() {
    return posthog;
}
